export const HOOK_LABEL = {
  record: 'Voice cash-log (record-keeping)',
  statement: 'Statement reconciliation',
  utility: 'Bulk utility payments',
  fastag: 'FASTag / fleet recharges',
  collect: 'Recurring / intent collections',
  xborder: 'Cross-border collections',
  vendor: 'Vendor payments',
  airecon: 'AI reconciliation at scale',
};

export const HOOK_PRIORITY = {
  airecon: 8, xborder: 7, vendor: 6,
  fastag: 5, utility: 5, collect: 5,
  statement: 2, record: 1,
};

export const TIER = {
  record: 0, statement: 0,
  utility: 1, fastag: 1, collect: 1,
  xborder: 2, vendor: 2,
  airecon: 3,
};

export const STYLE = {
  INVEST:    { t: 'Invest',         c: 'var(--go)',      bg: 'var(--go-bg)' },
  SELFSERVE: { t: 'Self-serve',     c: 'var(--serve)',   bg: 'var(--serve-bg)' },
  NURTURE:   { t: 'Nurture',        c: 'var(--nurture)', bg: 'var(--nurture-bg)' },
  DROP:      { t: 'Drop',           c: 'var(--drop)',    bg: 'var(--drop-bg)' },
  WAIT:      { t: 'Awaiting input', c: '#9a9cb0',        bg: '#eef0f4' },
};

export function scoreOf({ volume, money, buyer, pain, geo, effort }) {
  let s = 0;
  s += ({ neg: 0, small: 10, mid: 20, high: 25 })[volume] || 0;
  s += ({ thin: 5, mod: 15, strong: 25 })[money] || 0;
  s += ({ none: 0, influencer: 10, direct: 20 })[buyer] || 0;
  s += ({ none: 0, some: 8, core: 15 })[pain] || 0;
  s += ({ tier: 4, metro: 7, uae: 10 })[geo] || 0;
  s += ({ high: 0, med: 3, low: 5 })[effort] || 0;
  return s;
}

export function leadWedge(hk, kind) {
  if (!hk.length) return '—';
  const sorted = [...hk].sort((a, b) => HOOK_PRIORITY[b] - HOOK_PRIORITY[a]);
  if (kind === 'SELFSERVE') {
    const t0 = hk.filter(h => TIER[h] === 0).sort((a, b) => HOOK_PRIORITY[b] - HOOK_PRIORITY[a]);
    if (t0.length) return HOOK_LABEL[t0[0]];
  }
  return HOOK_LABEL[sorted[0]];
}

export function decide({ type, hooks, volume, money, buyer, pain, geo, effort }) {
  const hk = hooks;
  const sc = scoreOf({ volume, money, buyer, pain, geo, effort });

  const ready = type && hk.length > 0 && volume && money;
  const tiers = hk.map(h => TIER[h]);
  const hasT1 = hk.some(h => TIER[h] === 1);
  const hasT2 = hk.some(h => TIER[h] === 2);
  const onlyT0 = hk.length > 0 && tiers.every(t => t === 0);
  const volMid = volume === 'mid' || volume === 'high';

  let kind = 'WAIT', motion = '', owner = '', reasons = [];

  if (ready) {
    if (type === 'partner') {
      kind = 'INVEST'; motion = 'Partnerships — white-label'; owner = 'Owner: Samar / Priya';
      reasons.push('Platform/aggregator → embed FiniGenie behind their brand; score their end-users, not them.');
    } else if (type === 'enterprise' || hk.includes('airecon')) {
      kind = 'INVEST'; motion = 'Senior BD — long cycle'; owner = 'Owner: Priya (enterprise)';
      reasons.push('Enterprise/PSU reconciliation → senior BD, 3–9 month cycle.');
      reasons.push('Confirm the reconciliation slice is demo-able before the first decision-maker meeting.');
    } else if (hasT1 && volMid) {
      kind = 'INVEST'; motion = 'Inside sales — light touch'; owner = 'Owner: Shalini (volume desk)';
      reasons.push('Transactional volume on a payment wedge → BBPS commission upside (utility ₹2–10/txn, FASTag ~0.2%, recharge 1–4.5%).');
    } else if ((hasT2 || (hk.includes('statement') && (type === 'sme' || type === 'chain'))) && buyer !== 'none' && (money === 'mod' || money === 'strong')) {
      kind = 'INVEST'; motion = 'Sales-led — finance-ops subscription'; owner = 'Owner: Priya / Shalini';
      reasons.push('Finance-ops pain + reachable buyer + real monetisation → worth a proper sales cycle.');
    } else if (onlyT0 || (type === 'micro' && money === 'thin')) {
      kind = 'SELFSERVE'; motion = 'Product-led — no sales time'; owner = 'Route to free app + nurture sequence';
      reasons.push('Bottom-of-pyramid record-keeping → acquire free via voice cash-log, build data, upsell later.');
      reasons.push('Cost to serve outweighs payoff for outbound selling — let the product do the work.');
    } else if (buyer === 'none' && pain === 'none') {
      kind = 'DROP'; motion = 'No fit today'; owner = '';
      reasons.push('No reachable buyer and no working-capital pain → nothing to sell into right now.');
    } else {
      kind = sc >= 45 ? 'INVEST' : sc >= 30 ? 'NURTURE' : 'DROP';
      motion = kind === 'INVEST' ? 'Sales-led' : kind === 'NURTURE' ? 'Keep warm — low priority' : 'No fit today';
      owner = kind === 'INVEST' ? 'Owner: BD' : '';
      reasons.push(`Mixed signals — routed on fit score (${sc}/100).`);
    }

    // downgrade guards
    if (kind === 'INVEST' && buyer === 'none' && type !== 'partner') {
      kind = 'NURTURE'; motion = 'Keep warm — find a buyer first'; owner = '';
      reasons.unshift('No reachable economic buyer yet → nurture until you have one; don\'t burn outbound time.');
    }
    if (kind === 'INVEST' && sc < 30 && type !== 'enterprise') {
      kind = 'NURTURE'; motion = 'Keep warm — low priority'; owner = '';
    }

    // contextual reasons
    if (geo === 'uae') reasons.push('UAE + e-invoicing pilot (live from Jul 2026) → strong timing tailwind; prioritise.');
    if (pain === 'core' && (type === 'sme' || type === 'chain' || type === 'enterprise')) reasons.push('Deliver-first pain → future financing hook once trade data flows.');
    if (volume === 'high' && kind === 'INVEST') reasons.push('High volume → strongest commission/retention case; move fast.');
    if (effort === 'high' && kind !== 'SELFSERVE') reasons.push('High onboarding effort — make sure the deal size justifies engineering time.');
  } else {
    reasons.push('Add who they are, at least one hook, scale and monetisation to get a call.');
  }

  return { kind, motion, owner, reasons, score: sc, hooks: hk, ready };
}
